import { useState, useEffect, useCallback, useRef, createContext, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

const adminFetch = async (path, options = {}) => {
  const res = await fetch(`${BASE}${path}`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('token')}`,
    },
    ...options,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Error');
  return data;
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmt = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' });
};

const countdown = (expiresAt) => {
  if (!expiresAt) return '';
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return 'expirado';
  const m = Math.floor(ms / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return `${m}m ${s}s`;
};

const fmtDuration = (secs) => {
  if (!secs || secs < 60) return '< 1 min';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m} min`;
};

const isActive = (lastSeen) => {
  if (!lastSeen) return false;
  return Date.now() - new Date(lastSeen).getTime() < 5 * 60 * 1000;
};

// ── Accordion section wrapper ─────────────────────────────────────────────────

function AdminAccordion({ title, badge, badgeVariant = '', defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`admin-accordion${open ? ' admin-accordion--open' : ''}`}>
      <button className="admin-accordion-header" onClick={() => setOpen((o) => !o)}>
        <span className="admin-accordion-title">
          {title}
          {badge != null && (
            <span className={`admin-badge${badgeVariant ? ` admin-badge--${badgeVariant}` : ''}`}>
              {badge}
            </span>
          )}
        </span>
        <span className="admin-accordion-chevron">{open ? '▲' : '▼'}</span>
      </button>
      {open && <div className="admin-accordion-body">{children}</div>}
    </div>
  );
}

// ── Sub-sections ──────────────────────────────────────────────────────────────

function ActiveSessionsContent({ adminData }) {
  const sessions = adminData?.activeSessions ?? [];
  if (sessions.length === 0) return <p className="admin-empty">Sin actividad en los últimos 5 min.</p>;
  return (
    <table className="admin-table">
      <thead>
        <tr><th>Usuario</th><th>IP</th><th>Sesión</th><th>Viendo</th><th>Última actividad</th></tr>
      </thead>
      <tbody>
        {sessions.map((s, i) => (
          <tr key={i}>
            <td>{s.username}</td>
            <td><code className="admin-ip">{s.ip}</code></td>
            <td className="admin-metric-num">{fmtDuration(s.sessionSeconds)}</td>
            <td className="admin-watching">
              {s.currentAnimeTitle
                ? <span className="admin-tag admin-tag--watching">{s.currentAnimeTitle}</span>
                : <span className="admin-dim">—</span>}
            </td>
            <td>{fmt(s.lastSeen)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function SharingAlertsContent({ adminData }) {
  const alerts = adminData?.alerts ?? [];
  if (alerts.length === 0) return <p className="admin-empty">Sin alertas en las últimas 24 h.</p>;
  return (
    <table className="admin-table">
      <thead><tr><th>Usuario</th><th>Aviso</th><th>Suspensión</th><th>Expira</th><th>Razón</th></tr></thead>
      <tbody>
        {alerts.map((a, i) => (
          <tr key={i}>
            <td>{a.username}</td>
            <td>{fmt(a.warnedAt)}</td>
            <td>{fmt(a.suspendedAt)}</td>
            <td>{a.expiresAt ? countdown(a.expiresAt) : '—'}</td>
            <td>{a.reason || '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function UserManagementContent() {
  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminFetch('/admin/users');
      setUsers(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const suspend = async (id) => {
    try {
      await adminFetch(`/admin/users/${id}/suspend`, { method: 'POST' });
      load();
    } catch (err) { alert(err.message); }
  };

  const liftSuspension = async (id) => {
    try {
      await adminFetch(`/admin/users/${id}/lift-suspension`, { method: 'POST' });
      load();
    } catch (err) { alert(err.message); }
  };

  const deleteUser = async (id, username) => {
    if (!window.confirm(`¿Eliminar a ${username}? Esta acción es irreversible.`)) return;
    try {
      await adminFetch(`/admin/users/${id}`, { method: 'DELETE' });
      load();
    } catch (err) { alert(err.message); }
  };

  if (loading) return <p className="admin-empty">Cargando…</p>;

  return (
    <>
      {error && <p className="ferror">{error}</p>}
      <table className="admin-table">
        <thead>
          <tr>
            <th>Usuario</th><th>Email</th><th>Rol</th><th>Estado</th><th>Registro</th><th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id} className={u.is_suspended ? 'admin-row--suspended' : ''}>
              <td>
                <img src={u.avatar_url} alt="" className="admin-avatar" />
                {u.username}
              </td>
              <td>{u.email}</td>
              <td><span className={`admin-role admin-role--${u.role}`}>{u.role}</span></td>
              <td>{u.is_suspended ? <span className="admin-tag admin-tag--suspended">Suspendido</span> : <span className="admin-tag admin-tag--ok">Activo</span>}</td>
              <td>{fmt(u.created_at)}</td>
              <td className="admin-actions">
                {u.is_suspended
                  ? <button className="admin-btn admin-btn--sm" onClick={() => liftSuspension(u.id)}>Levantar</button>
                  : <button className="admin-btn admin-btn--sm admin-btn--warn" onClick={() => suspend(u.id)}>Suspender</button>
                }
                {u.role !== 'admin' && (
                  <button className="admin-btn admin-btn--sm admin-btn--danger" onClick={() => deleteUser(u.id, u.username)}>Eliminar</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function InvitationsContent() {
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [generating, setGenerating]   = useState(false);
  const [newCode, setNewCode]         = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminFetch('/admin/invitations');
      setInvitations(data);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    setNewCode(null);
    try {
      const data = await adminFetch('/admin/invitations', { method: 'POST' });
      setNewCode(data.code);
      load();
    } catch (err) { alert(err.message); }
    finally { setGenerating(false); }
  };

  const revoke = async (id) => {
    try {
      await adminFetch(`/admin/invitations/${id}`, { method: 'DELETE' });
      load();
    } catch (err) { alert(err.message); }
  };

  const copyCode = (code) => navigator.clipboard?.writeText(code).catch(() => {});

  return (
    <>
      <div className="admin-inline-actions">
        <button className="admin-btn" onClick={generate} disabled={generating}>
          {generating ? 'Generando…' : '+ Generar código'}
        </button>
      </div>

      {newCode && (
        <div className="admin-new-code" onClick={() => copyCode(newCode)} title="Clic para copiar">
          <span>{newCode}</span>
          <small>Clic para copiar</small>
        </div>
      )}

      {loading ? (
        <p className="admin-empty">Cargando…</p>
      ) : (
        <table className="admin-table">
          <thead><tr><th>Código</th><th>Creado</th><th>Expira</th><th>Usado por</th><th>Usado</th><th></th></tr></thead>
          <tbody>
            {invitations.map((inv) => (
              <tr key={inv.id} className={inv.used_by ? 'admin-row--used' : ''}>
                <td className="admin-code">{inv.code}</td>
                <td>{fmt(inv.created_at)}</td>
                <td>{fmt(inv.expires_at)}</td>
                <td>{inv.used_by || '—'}</td>
                <td>{fmt(inv.used_at)}</td>
                <td>
                  {!inv.used_by && (
                    <button className="admin-btn admin-btn--sm admin-btn--danger" onClick={() => revoke(inv.id)}>
                      Revocar
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {invitations.length === 0 && (
              <tr><td colSpan={6} className="admin-empty">Sin invitaciones</td></tr>
            )}
          </tbody>
        </table>
      )}
    </>
  );
}

const JOBS = [
  { key: 'syncCatalog',       label: 'Sync Catálogo (FLV)',      desc: 'Importa animes de AnimeFLV (deshabilitada)' },
  { key: 'syncCatalogAV1',    label: 'Sync Catálogo AV1',        desc: 'Importa animes de AniVi/AV1' },
  { key: 'syncCatalogJK',     label: 'Sync Catálogo JK',         desc: 'Importa animes de JKAnime' },
  { key: 'syncEpisodes',      label: 'Sync Episodios',           desc: 'Episodios de la fuente principal (AV1/JK si FLV está caída)' },
  { key: 'syncEpisodesMulti', label: 'Sync Episodios Multi',     desc: 'Episodios AV1 + JK (airing)' },
  { key: 'enrichCatalog',     label: 'Enriquecer catálogo',      desc: 'Jikan/MAL: scores, géneros, etc.' },
  { key: 'resolveRelations',  label: 'Resolver relaciones',      desc: 'Precuelas / secuelas entre animes' },
  { key: 'enrichMedia',       label: 'Enriquecer medios',        desc: 'Sinopsis AV1/JK, portadas faltantes, banners Kitsu' },
  { key: 'matchSlugs',        label: 'Match Slugs',              desc: 'Emparejar slugs entre fuentes' },
];

function SystemToolsContent({ adminData }) {
  const [logs,    setLogs]    = useState({});   // job → string[]
  const [running, setRunning] = useState(new Set());

  // Escuchar eventos job_output / job_done que llegan por WS en adminData
  useEffect(() => {
    if (!adminData) return;
    if (adminData.type === 'job_output') {
      const { job, line, level } = adminData;
      setLogs((prev) => ({
        ...prev,
        [job]: [...(prev[job] || []), { line, level }].slice(-200),
      }));
    }
    if (adminData.type === 'job_done') {
      const { job, code } = adminData;
      setLogs((prev) => ({
        ...prev,
        [job]: [...(prev[job] || []), { line: `— Proceso terminó con código ${code} —`, level: code === 0 ? 'done' : 'error' }],
      }));
      setRunning((prev) => { const s = new Set(prev); s.delete(job); return s; });
    }
  }, [adminData]);

  const runJob = async (key) => {
    setRunning((prev) => new Set([...prev, key]));
    setLogs((prev) => ({ ...prev, [key]: [{ line: `▶ Iniciando ${key}…`, level: 'info' }] }));
    try {
      await adminFetch('/admin/run-job', { method: 'POST', body: JSON.stringify({ job: key }) });
    } catch (err) {
      setLogs((prev) => ({ ...prev, [key]: [{ line: `Error: ${err.message}`, level: 'error' }] }));
      setRunning((prev) => { const s = new Set(prev); s.delete(key); return s; });
    }
  };

  return (
    <div className="admin-jobs-grid">
      {JOBS.map(({ key, label, desc }) => {
        const isRunning  = running.has(key);
        const jobLogs    = logs[key] || [];
        const hasLogs    = jobLogs.length > 0;
        return (
          <div key={key} className={`admin-job-card${isRunning ? ' admin-job-card--running' : ''}`}>
            <div className="admin-job-header">
              <div>
                <span className="admin-job-name">{label}</span>
                <span className="admin-job-desc">{desc}</span>
              </div>
              <button
                className={`admin-btn admin-btn--sm${isRunning ? '' : ' admin-btn--run'}`}
                onClick={() => runJob(key)}
                disabled={isRunning}
              >
                {isRunning ? '⟳ Corriendo…' : '▶ Ejecutar'}
              </button>
            </div>
            {hasLogs && (
              <div className="admin-job-log">
                {jobLogs.map((entry, i) => (
                  <div key={i} className={`admin-job-line admin-job-line--${entry.level}`}>
                    {entry.line}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function UserActivityContent() {
  const [activity, setActivity] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const data = await adminFetch('/admin/user-activity');
      setActivity(data);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, 30_000);
    return () => clearInterval(timerRef.current);
  }, [load]);

  if (loading) return <p className="admin-empty">Cargando…</p>;

  return (
    <>
      <div className="admin-refresh-hint">Actualiza cada 30 s</div>
      <table className="admin-table">
        <thead>
          <tr>
            <th>Usuario</th>
            <th>Estado</th>
            <th>Duración sesión</th>
            <th>Último visto</th>
            <th>Anime actual / último</th>
          </tr>
        </thead>
        <tbody>
          {activity.map((u) => {
            const active      = isActive(u.lastSeen);
            const animeLabel  = u.currentAnimeTitle || u.lastWatchedTitle;
            const animeSource = u.currentAnimeTitle ? 'now' : 'history';
            return (
              <tr key={u.id} className={active ? 'admin-row--active' : ''}>
                <td>
                  <img src={u.avatar_url} alt="" className="admin-avatar" />
                  {u.username}
                </td>
                <td>
                  {active
                    ? <span className="admin-tag admin-tag--ok">● En línea</span>
                    : <span className="admin-tag">Offline</span>}
                </td>
                <td className="admin-metric-num">
                  {active ? fmtDuration(u.sessionSeconds) : '—'}
                </td>
                <td>{fmt(u.lastSeen)}</td>
                <td className="admin-watching">
                  {animeLabel
                    ? <>
                        <span className={`admin-tag admin-tag--${animeSource === 'now' ? 'watching' : 'hist'}`}>
                          {animeSource === 'now' ? '▶ ' : ''}
                          {animeLabel}
                        </span>
                        {animeSource === 'history' && u.lastWatchedAt && (
                          <span className="admin-dim"> · {fmt(u.lastWatchedAt)}</span>
                        )}
                      </>
                    : <span className="admin-dim">Sin actividad</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function Admin() {
  const { user, adminData } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && !user.is_admin) navigate('/', { replace: true });
  }, [user, navigate]);

  if (!user?.is_admin) return null;

  const uniqueActive = [...new Map(
    (adminData?.activeSessions ?? []).map((s) => [s.userId, s])
  ).values()].length;

  return (
    <div className="admin-page">

      {/* ── Cabecera ── */}
      <div className="admin-header">
        <h1>Panel de administración</h1>
        {adminData ? (
          <span className="admin-ws-status admin-ws-status--on">● En vivo</span>
        ) : (
          <span className="admin-ws-status admin-ws-status--off">○ Conectando…</span>
        )}
      </div>

      {/* ── Layout dos columnas ── */}
      <div className="admin-grid">

        {/* ── Columna izquierda ── */}
        <div className="admin-col">
          <AdminAccordion title="Sesiones activas" badge={uniqueActive} defaultOpen>
            <ActiveSessionsContent adminData={adminData} />
          </AdminAccordion>

          <AdminAccordion
            title="Alertas de compartición"
            badge={(adminData?.alerts ?? []).length || undefined}
            badgeVariant={(adminData?.alerts ?? []).length > 0 ? 'warn' : ''}
            defaultOpen
          >
            <SharingAlertsContent adminData={adminData} />
          </AdminAccordion>

          <AdminAccordion title="Gestión de usuarios" defaultOpen>
            <UserManagementContent />
          </AdminAccordion>

          <AdminAccordion title="Invitaciones" defaultOpen={false}>
            <InvitationsContent />
          </AdminAccordion>
        </div>

        {/* ── Columna derecha ── */}
        <div className="admin-col">
          <AdminAccordion title="Actividad por usuario" defaultOpen>
            <UserActivityContent />
          </AdminAccordion>

          <AdminAccordion title="Herramientas del sistema" defaultOpen={false}>
            <SystemToolsContent adminData={adminData} />
          </AdminAccordion>
        </div>

      </div>
    </div>
  );
}
